import {
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from 'react-bootstrap'
import { nineNineIntegratorApi } from 'src/lib/axios'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import Cart from '../../../types/cart'
import { useContext, useEffect, useState } from 'react'
import { CartsContext } from '@context/cart.ctx'
import { AppContext } from '@context/app.ctx'
import { zodResolver } from '@hookform/resolvers/zod'

interface NineNineOrderModalCancelProps {
  openModal: boolean
  cart: Cart
  closeModal: () => void
}

const cancelOrderSchema = z.object({
  reason_id: z.number().min(1, 'Selecione um motivo de cancelamento'),
  reason: z.string().min(10, 'O motivo deve ter pelo menos 10 caracteres'),
})

type CancelOrderFormData = z.infer<typeof cancelOrderSchema>
const CANCEL_REASONS = [
  { id: 1010, label: 'Item esgotado' },
  { id: 1020, label: 'Loja fechada' },
  { id: 1030, label: 'A loja está muito ocupada para preparar a refeição' },
  {
    id: 1040,
    label:
      'Não é possível preparar a refeição devido a falta de água ou energia',
  },
  { id: 1050, label: 'Cancelamento solicitado pelo cliente' },
  { id: 1060, label: 'Sem entregador para entrega' },
  { id: 1080, label: 'Outro motivo' },
] as const

export const NineNineOrderCancelModal = ({
  cart,
  openModal,
  closeModal,
}: NineNineOrderModalCancelProps) => {
  const { setNineNineOrders, setCart } = useContext(CartsContext)
  const { handleShowToast } = useContext(AppContext)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CancelOrderFormData>({
    resolver: zodResolver(cancelOrderSchema),
    defaultValues: {
      reason_id: 0,
      reason: '',
    },
  })

  const handleCancelOrder = async (data: CancelOrderFormData, cart: Cart) => {
    setIsCanceling(true)
    try {
      await nineNineIntegratorApi.post(`/orders/update/${cart.orderId}`, {
        dispatchType: 'CANCELLED',
        reason_id: data.reason_id,
        reason: data.reason,
      })
      // update nineNineOrders state
      setNineNineOrders((prev: any[]) =>
        prev.map((order) => {
          if (order.id === cart.id) order.status = 'canceled'
          return order
        })
      )

      // Atualizar o cart com o novo status
      cart.status = 'canceled'
      setCart(cart)

      // Mostrar toast de sucesso
      handleShowToast({
        type: 'success',
        title: '99Food',
        content: 'Pedido cancelado com sucesso!',
        size: 25,
      })

      setShowCancelModal(false)
      reset()
      closeModal()
    } catch (error) {
      console.error('Erro ao cancelar pedido 99Food', error)
      handleShowToast({
        type: 'erro',
        title: '99Food',
        content: 'Falha ao cancelar o pedido. Tente novamente.',
        size: 25,
      })
    } finally {
      setIsCanceling(false)
    }
  }
  const handleCloseCancelModal = () => {
    setShowCancelModal(false)
    reset()
    closeModal()
  }

  useEffect(() => {
    setShowCancelModal(openModal)
  }, [openModal])

  return (
    <Modal
      show={showCancelModal}
      onHide={handleCloseCancelModal}
      centered
      backdrop="static"
      keyboard={false}
      style={{ zIndex: 999999 }}
    >
    <ModalHeader>
      <ModalTitle className="fw-bold">Confirmar Cancelamento</ModalTitle>
    </ModalHeader>

    <ModalBody className="p-4">
      <p className="mb-3">Preencha os dados para cancelar o pedido:</p>

      <Form
        id="cancel-order-form"
        onSubmit={handleSubmit((data) => handleCancelOrder(data, cart!))}
      >
        <div className="mb-3">
          <label htmlFor="reason_id" className="form-label fw-medium">
            Motivo do cancelamento:
          </label>
          <select
            {...register('reason_id', { valueAsNumber: true })}
            className={`form-select ${errors.reason_id ? 'is-invalid' : ''}`}
          >
            <option value={0}>Selecione um motivo</option>
            {CANCEL_REASONS.map((reason) => (
              <option key={reason.id} value={reason.id}>
                {reason.label}
              </option>
            ))}
          </select>
          {errors.reason_id && (
            <div className="invalid-feedback">{errors.reason_id.message}</div>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="reason" className="form-label fw-medium">
            Motivo detalhado:
          </label>
          <textarea
            {...register('reason')}
            className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
            rows={4}
            placeholder="Descreva o motivo do cancelamento (mínimo 10 caracteres)..."
          />
          {errors.reason && (
            <div className="invalid-feedback">{errors.reason.message}</div>
          )}
          <div className="form-text">
            {watch('reason')?.length || 0}/10 caracteres mínimos
          </div>
        </div>

        <div className="alert alert-warning">
          <small>
            <strong>Atenção:</strong> Esta ação não pode ser desfeita. O pedido
            será cancelado permanentemente.
          </small>
        </div>
      </Form>
    </ModalBody>

    <ModalFooter className="border-top">
      <div className="d-flex w-100 gap-2">
        <button
          type="button"
          onClick={handleCloseCancelModal}
          className="btn btn-outline-secondary flex-fill fw-semibold"
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="cancel-order-form"
          className="btn btn-danger flex-fill fw-semibold"
          disabled={isCanceling}
        >
          {isCanceling ? 'Cancelando...' : 'Confirmar Cancelamento'}
        </button>
      </div>
    </ModalFooter>
  </Modal>
  )
}
